import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MdOutlineAccountBalanceWallet } from "react-icons/md";
import { ImArrowUp } from "react-icons/im";
import { SiCashapp } from "react-icons/si";
import { GiPlainArrow } from "react-icons/gi";
import { FaBars, FaTimes, FaChevronDown, FaSignOutAlt, FaUsers, FaMoneyCheckAlt, FaFileInvoice, FaUserPlus, FaCreditCard } from 'react-icons/fa';
import { RiDashboard2Line } from 'react-icons/ri';
import { MdOutlinePayments, MdCleaningServices } from 'react-icons/md';
import { HiOutlineDocumentReport } from 'react-icons/hi';
import { BiMoneyWithdraw } from 'react-icons/bi';
import { TbCategoryPlus } from "react-icons/tb";
import { IoSettings } from "react-icons/io5";
import { IoIosAddCircle } from "react-icons/io";
import { FaListUl } from "react-icons/fa6";
import { useAuth } from '../../contexts/AuthContext';

export default function Sidebar() {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      
      // Close sidebar when switching to desktop view
      if (!mobile && isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  const toggleSidebar = () => setIsOpen(!isOpen);
  const toggleDropdown = (menu) => {
    setDropdownOpen((prev) => ({ ...prev, [menu]: !prev[menu] }));
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Close sidebar when a link is clicked on mobile
  const handleLinkClick = () => {
    if (isMobile) {
      setIsOpen(false);
    }
  };

  return (
    <>
      {/* Mobile toggle button */}
      <div className="md:hidden flex items-center justify-between bg-black text-white px-4 py-3 fixed top-0 left-0 right-0 z-50">
        <h1 className="text-lg font-bold">Admin Panel</h1>
        <button onClick={toggleSidebar} className="p-2 focus:outline-none">
          {isOpen ? <FaBars size={20} /> : <FaBars size={20} />}
        </button>
      </div>

      {/* Overlay for mobile */}
      {isOpen && isMobile && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={toggleSidebar}
        ></div>
      )}

      {/* Sidebar */}
      <div 
        className={`fixed top-0 left-0 h-full w-64 bg-black text-white z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0 md:fixed md:h-full`}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h2 className="text-xl font-semibold">Menu</h2>
          <button onClick={toggleSidebar} className="md:hidden p-1">
            <FaTimes size={18} />
          </button>
        </div>

        <nav className="p-4 space-y-2 flex-1 overflow-y-auto h-[calc(100%-140px)]">
          <Link to="/" className="flex items-center py-2 px-4 hover:bg-gray-800 rounded" onClick={handleLinkClick}>
            <RiDashboard2Line className="mr-2" /> Dashboard
          </Link>
          {/* Cashbook Dropdown */}
          <div>
            <button
              onClick={() => toggleDropdown('cashbook')}
              className="w-full text-left py-2 px-4 flex items-center justify-between hover:bg-gray-800 rounded"
            >
              <span className="flex items-center">
                <SiCashapp className="mr-2" /> Cashbook
              </span>
              <FaChevronDown className={`transform transition-transform duration-200 ${dropdownOpen.cashbook ? 'rotate-180' : ''}`} />
            </button>

            <div
              className={`ml-6 space-y-1 mt-1 overflow-hidden transition-all duration-300 ease-in-out ${
                dropdownOpen.cashbook ? 'max-h-60' : 'max-h-0'
              }`}
            >
              <Link to="/cashbook" className="flex items-center px-2 py-1 hover:bg-gray-800 rounded" onClick={handleLinkClick}>
                <MdOutlineAccountBalanceWallet className="mr-2" /> Open
              </Link>
              <Link to="/cashin" className="flex items-center px-2 py-1 hover:bg-gray-800 rounded" onClick={handleLinkClick}>
                <GiPlainArrow className="mr-2" /> Cash In
              </Link>
              <Link to="/cashout" className="flex items-center px-2 py-1 hover:bg-gray-800 rounded" onClick={handleLinkClick}>
                <ImArrowUp className="mr-2" /> Cash Out
              </Link>
            </div>
          </div>
          {/* Clients Dropdown */}
          <div>
            <button
              onClick={() => toggleDropdown('clients')}
              className="w-full text-left py-2 px-4 flex items-center justify-between hover:bg-gray-800 rounded"
            >
              <span className="flex items-center">
                <FaUsers className="mr-2" /> Clients
              </span>
              <FaChevronDown className={`transform transition-transform duration-200 ${dropdownOpen.clients ? 'rotate-180' : ''}`} />
            </button>

            <div
              className={`ml-6 space-y-1 mt-1 overflow-hidden transition-all duration-300 ease-in-out ${
                dropdownOpen.clients ? 'max-h-60' : 'max-h-0'
              }`}
            >
              <Link to="/invoices" className="flex items-center px-2 py-1 hover:bg-gray-800 rounded" onClick={handleLinkClick}>
                <FaFileInvoice className="mr-2" /> Invoices
              </Link>
              <Link to="/" className="flex items-center px-2 py-1 hover:bg-gray-800 rounded" onClick={handleLinkClick}>
                <MdOutlinePayments className="mr-2" /> Payment Received
              </Link>
              <Link to="/clients/list" className="flex items-center px-2 py-1 hover:bg-gray-800 rounded" onClick={handleLinkClick}>
                <FaUsers className="mr-2" /> Client List
              </Link>
              <Link to="/clients/register" className="flex items-center px-2 py-1 hover:bg-gray-800 rounded" onClick={handleLinkClick}>
                <FaUserPlus className="mr-2" /> Register Clients
              </Link>
            </div>
          </div>

          {/* Expenses Dropdown */}
          <div>
            <button
              onClick={() => toggleDropdown('expenses')}
              className="w-full text-left py-2 px-4 flex items-center justify-between hover:bg-gray-800 rounded"
            >
              <span className="flex items-center">
                <FaMoneyCheckAlt className="mr-2" /> Expenses
              </span>
              <FaChevronDown className={`transform transition-transform duration-200 ${dropdownOpen.expenses ? 'rotate-180' : ''}`} />
            </button>
            <div
              className={`ml-6 space-y-1 mt-1 overflow-hidden transition-all duration-300 ease-in-out ${
                dropdownOpen.expenses ? 'max-h-40' : 'max-h-0'
              }`}
            >
              <Link to="/" className="flex items-center px-2 py-1 hover:bg-gray-800 rounded" onClick={handleLinkClick}>
                <IoIosAddCircle className="mr-2" /> Add
              </Link>
              <Link to="/" className="flex items-center px-2 py-1 hover:bg-gray-800 rounded" onClick={handleLinkClick}>
                <FaListUl className="mr-2" /> List
              </Link>
            </div>
          </div>

          {/* Revenue Dropdown */}
          <div>
            <button
              onClick={() => toggleDropdown('revenue')}
              className="w-full text-left py-2 px-4 flex items-center justify-between hover:bg-gray-800 rounded"
            >
              <span className="flex items-center">
                <FaMoneyCheckAlt className="mr-2" /> Revenue
              </span>
              <FaChevronDown className={`transform transition-transform duration-200 ${dropdownOpen.revenue ? 'rotate-180' : ''}`} />
            </button>
            <div
              className={`ml-6 space-y-1 mt-1 overflow-hidden transition-all duration-300 ease-in-out ${
                dropdownOpen.revenue ? 'max-h-40' : 'max-h-0'
              }`}
            >
              <Link to="/" className="flex items-center px-2 py-1 hover:bg-gray-800 rounded" onClick={handleLinkClick}>
                <IoIosAddCircle className="mr-2" /> Add
              </Link>
              <Link to="/" className="flex items-center px-2 py-1 hover:bg-gray-800 rounded" onClick={handleLinkClick}>
                <FaListUl className="mr-2" /> List
              </Link>
            </div>
          </div>

          {/* Liabilities Dropdown */}
          <div>
            <button
              onClick={() => toggleDropdown('liabilities')}
              className="w-full text-left py-2 px-4 flex items-center justify-between hover:bg-gray-800 rounded"
            >
              <span className="flex items-center">
                <FaCreditCard className="mr-2" /> Liabilities
              </span>
              <FaChevronDown className={`transform transition-transform duration-200 ${dropdownOpen.liabilities ? 'rotate-180' : ''}`} />
            </button>
            <div
              className={`ml-6 space-y-1 mt-1 overflow-hidden transition-all duration-300 ease-in-out ${
                dropdownOpen.liabilities ? 'max-h-40' : 'max-h-0'
              }`}
            >
              <Link to="/" className="flex items-center px-2 py-1 hover:bg-gray-800 rounded" onClick={handleLinkClick}>
                <FaCreditCard className="mr-2" /> Loans / Payables
              </Link>
              <Link to="/" className="flex items-center px-2 py-1 hover:bg-gray-800 rounded" onClick={handleLinkClick}>
                <FaCreditCard className="mr-2" /> Credit Card Balances
              </Link>
            </div>
          </div>

          {/* Reports Dropdown */}
          <div>
            <button
              onClick={() => toggleDropdown('reports')}
              className="w-full text-left py-2 px-4 flex items-center justify-between hover:bg-gray-800 rounded"
            >
              <span className="flex items-center">
                <HiOutlineDocumentReport className="mr-2" /> Reports
              </span>
              <FaChevronDown className={`transform transition-transform duration-200 ${dropdownOpen.reports ? 'rotate-180' : ''}`} />
            </button>
            <div
              className={`ml-6 space-y-1 mt-1 overflow-hidden transition-all duration-300 ease-in-out ${
                dropdownOpen.reports ? 'max-h-60' : 'max-h-0'
              }`}
            >
              <Link to="/" className="flex items-center px-2 py-1 hover:bg-gray-800 rounded" onClick={handleLinkClick}>
                <HiOutlineDocumentReport className="mr-2" /> Profit & Loss
              </Link>
              <Link to="/" className="flex items-center px-2 py-1 hover:bg-gray-800 rounded" onClick={handleLinkClick}>
                <HiOutlineDocumentReport className="mr-2" /> Cashbook
              </Link>
              <Link to="/" className="flex items-center px-2 py-1 hover:bg-gray-800 rounded" onClick={handleLinkClick}>
                <HiOutlineDocumentReport className="mr-2" /> Outstanding Invoices
              </Link>
            </div>
          </div>

          {/* Settings Dropdown */}
          <div>
            <button
              onClick={() => toggleDropdown('settings')}
              className="w-full text-left py-2 px-4 flex items-center justify-between hover:bg-gray-800 rounded"
            >
              <span className="flex items-center">
                <IoSettings className="mr-2" /> Settings
              </span>
              <FaChevronDown className={`transform transition-transform duration-200 ${dropdownOpen.settings ? 'rotate-180' : ''}`} />
            </button>
            <div
              className={`ml-6 space-y-1 mt-1 overflow-hidden transition-all duration-300 ease-in-out ${
                dropdownOpen.settings ? 'max-h-60' : 'max-h-0'
              }`}
            >
              <Link to="/expense-categories" className="flex items-center px-2 py-1 hover:bg-gray-800 rounded" onClick={handleLinkClick}>
                <TbCategoryPlus className="mr-2" /> Expense Categories
              </Link>
              <Link to="/revenue-categories" className="flex items-center px-2 py-1 hover:bg-gray-800 rounded" onClick={handleLinkClick}>
                <TbCategoryPlus className="mr-2" /> Revenue Categories
              </Link>
              <Link to="/services" className="flex items-center px-2 py-1 hover:bg-gray-800 rounded" onClick={handleLinkClick}>
                <MdCleaningServices className="mr-2" /> Services
              </Link>
            </div>
          </div>
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t border-gray-700">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center py-2 px-4 bg-red-600 text-white rounded hover:bg-red-700 transition-colors duration-200"
          >
            <FaSignOutAlt className="mr-2" />
            Logout
          </button>
        </div>
      </div>
    </>
  );
}